import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { TimePickerField } from "@/components/ui/time-picker-field";
import { Badge } from "@/components/ui/badge";
import { CalendarIcon, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";

type DayKey = "sun" | "mon" | "tue" | "wed" | "thu" | "fri" | "sat";
const DAYS: { key: DayKey; label: string }[] = [
  { key: "sun", label: "Sunday" },
  { key: "mon", label: "Monday" },
  { key: "tue", label: "Tuesday" },
  { key: "wed", label: "Wednesday" },
  { key: "thu", label: "Thursday" },
  { key: "fri", label: "Friday" },
  { key: "sat", label: "Saturday" },
];

interface WindowSpec { start: string; end: string }
interface Settings {
  timezone: string;
  slot_minutes: number;
  buffer_minutes: number;
  max_per_day: number;
  lead_time_minutes: number;
  horizon_days: number;
  weekly_hours: Record<DayKey, WindowSpec[]>;
  blackout_dates: string[];
}

const DEFAULTS: Settings = {
  timezone: "America/New_York",
  slot_minutes: 15,
  buffer_minutes: 5,
  max_per_day: 12,
  lead_time_minutes: 60,
  horizon_days: 14,
  weekly_hours: {
    sun: [],
    mon: [{ start: "09:00", end: "17:00" }],
    tue: [{ start: "09:00", end: "17:00" }],
    wed: [{ start: "09:00", end: "17:00" }],
    thu: [{ start: "09:00", end: "17:00" }],
    fri: [{ start: "09:00", end: "17:00" }],
    sat: [],
  },
  blackout_dates: [],
};

const TIMEZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
];

function isHHMM(s: string) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(s);
}

export function AvailabilitySettingsCard() {
  const { profile } = useProfile();
  const [settings, setSettings] = useState<Settings>(DEFAULTS);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    const raw = ((profile as any).availability_settings ?? {}) as Partial<Settings>;
    setSettings({
      ...DEFAULTS,
      ...raw,
      weekly_hours: { ...DEFAULTS.weekly_hours, ...(raw.weekly_hours ?? {}) } as Record<DayKey, WindowSpec[]>,
      blackout_dates: raw.blackout_dates ?? [],
    });
  }, [profile]);

  const blackoutDates = useMemo(
    () => settings.blackout_dates.map((d) => new Date(d + "T00:00:00")),
    [settings.blackout_dates],
  );

  function updateDayWindow(day: DayKey, idx: number, patch: Partial<WindowSpec>) {
    setSettings((s) => {
      const list = [...(s.weekly_hours[day] ?? [])];
      list[idx] = { ...list[idx], ...patch };
      return { ...s, weekly_hours: { ...s.weekly_hours, [day]: list } };
    });
  }

  function addDayWindow(day: DayKey) {
    setSettings((s) => ({
      ...s,
      weekly_hours: {
        ...s.weekly_hours,
        [day]: [...(s.weekly_hours[day] ?? []), { start: "09:00", end: "17:00" }],
      },
    }));
  }

  function removeDayWindow(day: DayKey, idx: number) {
    setSettings((s) => {
      const list = [...(s.weekly_hours[day] ?? [])];
      list.splice(idx, 1);
      return { ...s, weekly_hours: { ...s.weekly_hours, [day]: list } };
    });
  }

  function toggleDayEnabled(day: DayKey, enabled: boolean) {
    setSettings((s) => ({
      ...s,
      weekly_hours: {
        ...s.weekly_hours,
        [day]: enabled ? [{ start: "09:00", end: "17:00" }] : [],
      },
    }));
  }

  function toggleBlackout(date: Date | undefined) {
    if (!date) return;
    const ymd = format(date, "yyyy-MM-dd");
    setSettings((s) => {
      const has = s.blackout_dates.includes(ymd);
      return {
        ...s,
        blackout_dates: has
          ? s.blackout_dates.filter((d) => d !== ymd)
          : [...s.blackout_dates, ymd].sort(),
      };
    });
  }

  function removeBlackout(ymd: string) {
    setSettings((s) => ({ ...s, blackout_dates: s.blackout_dates.filter((d) => d !== ymd) }));
  }

  async function handleSave() {
    if (!profile) return;
    // Validate
    for (const { key, label } of DAYS) {
      for (const w of settings.weekly_hours[key] ?? []) {
        if (!isHHMM(w.start) || !isHHMM(w.end)) {
          toast.error(`${label}: invalid time format`);
          return;
        }
        if (w.start >= w.end) {
          toast.error(`${label}: start must be before end`);
          return;
        }
      }
    }
    if (settings.slot_minutes < 5 || settings.slot_minutes > 120) {
      toast.error("Slot length must be between 5 and 120 minutes");
      return;
    }
    if (settings.buffer_minutes < 0 || settings.buffer_minutes > 60) {
      toast.error("Buffer must be between 0 and 60 minutes");
      return;
    }
    if (settings.lead_time_minutes < 0 || settings.lead_time_minutes > 1440) {
      toast.error("Minimum lead time must be between 0 and 1440 minutes");
      return;
    }
    if (settings.max_per_day < 1 || settings.max_per_day > 50) {
      toast.error("Max appointments per day must be between 1 and 50");
      return;
    }
    if (settings.horizon_days < 1 || settings.horizon_days > 60) {
      toast.error("Booking horizon must be between 1 and 60 days");
      return;
    }

    setSaving(true);
    const { error } = await supabase
      .from("profiles")
      .update({ availability_settings: settings as any } as any)
      .eq("id", profile.id);
    setSaving(false);
    if (error) {
      toast.error("Failed to save availability");
      return;
    }
    toast.success("Availability saved");
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sarah's Availability</CardTitle>
        <p className="text-sm text-muted-foreground">
          Control when Sarah can book phone consultations on your behalf.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Scheduling rules */}
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-foreground">Scheduling rules</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label>Time zone</Label>
              <Select
                value={settings.timezone}
                onValueChange={(v) => setSettings((s) => ({ ...s, timezone: v }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map((tz) => (
                    <SelectItem key={tz} value={tz}>{tz.replace("_", " ")}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Slot length (minutes)</Label>
              <Select
                value={String(settings.slot_minutes)}
                onValueChange={(v) => setSettings((s) => ({ ...s, slot_minutes: Number(v) }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[10, 15, 20, 30, 45, 60].map((m) => (
                    <SelectItem key={m} value={String(m)}>{m} min</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Buffer between calls (minutes)</Label>
              <Input
                type="number"
                min={0}
                max={60}
                value={settings.buffer_minutes}
                onChange={(e) => setSettings((s) => ({ ...s, buffer_minutes: Number(e.target.value) || 0 }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Minimum lead time (minutes)</Label>
              <Input
                type="number"
                min={0}
                max={1440}
                value={settings.lead_time_minutes}
                onChange={(e) => setSettings((s) => ({ ...s, lead_time_minutes: Number(e.target.value) || 0 }))}
              />
              <p className="text-xs text-muted-foreground">How far in advance the soonest slot can be booked.</p>
            </div>
            <div className="space-y-1">
              <Label>Max appointments per day</Label>
              <Input
                type="number"
                min={1}
                max={50}
                value={settings.max_per_day}
                onChange={(e) => setSettings((s) => ({ ...s, max_per_day: Number(e.target.value) || 1 }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Booking horizon (days)</Label>
              <Input
                type="number"
                min={1}
                max={60}
                value={settings.horizon_days}
                onChange={(e) => setSettings((s) => ({ ...s, horizon_days: Number(e.target.value) || 1 }))}
              />
            </div>
          </div>
        </div>

        {/* Weekly hours */}
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-foreground">Weekly hours</h3>
          <div className="space-y-2">
            {DAYS.map(({ key, label }) => {
              const windows = settings.weekly_hours[key] ?? [];
              const enabled = windows.length > 0;
              return (
                <div key={key} className="rounded-md border p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Switch checked={enabled} onCheckedChange={(v) => toggleDayEnabled(key, v)} />
                      <Label className="font-medium">{label}</Label>
                    </div>
                    {enabled && (
                      <Button type="button" variant="ghost" size="sm" onClick={() => addDayWindow(key)}>
                        <Plus className="h-4 w-4 mr-1" /> Add window
                      </Button>
                    )}
                  </div>
                  {enabled && (
                    <div className="mt-3 space-y-2">
                      {windows.map((w, idx) => (
                        <div key={idx} className="flex items-center gap-2">
                          <TimePickerField
                            value={w.start}
                            onChange={(v) => updateDayWindow(key, idx, { start: v })}
                            className="w-32"
                          />
                          <span className="text-muted-foreground text-sm">to</span>
                          <TimePickerField
                            value={w.end}
                            onChange={(v) => updateDayWindow(key, idx, { end: v })}
                            className="w-32"
                          />
                          {windows.length > 1 && (
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeDayWindow(key, idx)}
                              aria-label="Remove window"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {!enabled && <p className="text-xs text-muted-foreground mt-2">Closed</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Blackout dates */}
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-foreground">Blackout dates</h3>
          <p className="text-xs text-muted-foreground">
            Holidays or PTO. Sarah will not offer any slots on these dates.
          </p>
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" className={cn("w-[260px] justify-start text-left font-normal")}>
                <CalendarIcon className="mr-2 h-4 w-4" />
                Pick dates to block
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="multiple"
                selected={blackoutDates}
                onSelect={(dates) => {
                  // react-day-picker passes the full array; rebuild
                  const arr = (dates ?? []) as Date[];
                  setSettings((s) => ({
                    ...s,
                    blackout_dates: arr.map((d) => format(d, "yyyy-MM-dd")).sort(),
                  }));
                }}
                disabled={(date) => date < new Date(new Date().toDateString())}
                initialFocus
                className={cn("p-3 pointer-events-auto")}
              />
            </PopoverContent>
          </Popover>

          {settings.blackout_dates.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {settings.blackout_dates.map((ymd) => (
                <Badge key={ymd} variant="secondary" className="gap-1">
                  {format(new Date(ymd + "T00:00:00"), "MMM d, yyyy")}
                  <button
                    type="button"
                    onClick={() => removeBlackout(ymd)}
                    className="ml-1 text-muted-foreground hover:text-foreground"
                    aria-label={`Remove ${ymd}`}
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">No blackout dates set.</p>
          )}
        </div>

        <Button type="button" onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : "Save Availability"}
        </Button>
      </CardContent>
    </Card>
  );
}

export default AvailabilitySettingsCard;